import {
  type AUDIENCE_MEMBERSHIP_SORT_KEYS,
  type AUDIENCE_SORT_KEYS,
} from '@/router/searchParams/audienceSearchParams';
import { type PAGINATION_PARAM_KEYS } from '@/router/searchParams/defaultPaginationParams';
import { type NavigableRouteType, type RouteSearchParamEnumType } from '@/router/types';

import { instance } from './appRoutes';

export const audienceImport = {
  path: `${instance.path}/audiences/import`,
  description:
    'A form to import a Salesforce or HubSpot audience by supplying a Salesforce report link or HubSpot Company Segment List link.',
  navigable: true,
  pathParams: {},
  searchParams: {},
} satisfies NavigableRouteType;

export const audienceCSVCreate = {
  path: `${instance.path}/audiences/upload`,
  description:
    'A form to either upload a CSV or manually type domain names which make up a audience.',
  navigable: true,
  pathParams: {},
  searchParams: {},
} satisfies NavigableRouteType;

export const audienceAIBasedCreate = {
  path: `${instance.path}/audiences/create-ai-based`,
  description:
    'A form to create an AI-based audience from either CRM profile training data or a custom profile audience.',
  navigable: true,
  pathParams: {},
  searchParams: {},
} satisfies NavigableRouteType;

export const audienceRuleBasedCreate = {
  path: `${instance.path}/audiences/create-rule-based`,
  description:
    'Legacy route for rule-based audience creation. Redirects to the rule builder route.',
  navigable: false,
  pathParams: {},
  searchParams: {},
} satisfies NavigableRouteType;

export const audienceRuleBuilderCreate = {
  path: `${instance.path}/audiences/create-rule-based`,
  description:
    'A form to create an audience with rule-based boolean logic based on company properties or events. Audiences can be created with rules such as whether a company is in a specific industry, region, or revenue range. It can also use behavior-based rules such as whether a company has visited a specific website or engaged with a specific marketing campaign.',
  navigable: true,
  pathParams: {},
  searchParams: {},
} satisfies NavigableRouteType;

export const audiences = {
  path: `${instance.path}/audiences`,
  description:
    'Displays a list of all audiences in the system. Each audience is shown with the number of companies in the audience, the number of visits, and the status of each audience. Status indicates whether an error occurred during CSV upload or CRM (Salesforce or HubSpot) ingestion.',
  examples: [
    'Show me a list of audiences sorted by size descending',
    'I would like to see all audiences that start with "EMEA"',
  ],
  navigable: true,
  pathParams: {},
  searchParams: {
    'a.page': {
      type: 'string',
      description: 'The page number to display',
    },
    'a.size': {
      type: 'string',
      description: 'The number of items to display per page',
    },
    'a.sortBy': {
      type: 'enum',
      enumValues: [
        'created_at',
        'created_by.name',
        'metrics.size',
        'metrics.visits',
        'name',
        'type',
      ],
      default: 'created_at',
      description: 'The field to sort by',
      required: false,
      requiresLookup: false,
    } satisfies RouteSearchParamEnumType<AUDIENCE_SORT_KEYS>,
    'a.sortOrder': {
      type: 'enum',
      enumValues: ['asc', 'desc'],
      default: 'desc',
      description: 'The sort order',
      required: false,
      requiresLookup: false,
    },
    'a.search': {
      type: 'string',
      description: 'A string to search for in the audience name',
      required: false,
      requiresLookup: false,
    },
  },
} satisfies NavigableRouteType<PAGINATION_PARAM_KEYS<'a'>>;

export const audienceDetail = {
  path: `${instance.path}/audiences/:audience`,
  description:
    'A detail page for an audience. Shows a list of companies in the audience, the number of visits of each company in the last 30 days and a date for the most recent visit.',
  navigable: true,
  pathParams: {
    audience: {
      type: 'string',
      description: 'The ID of the audience the user wishes to view',
      required: true,
      requiresLookup: true,
      lookupType: 'audience',
    },
  },
  searchParams: {
    'a.page': {
      type: 'string',
      description: 'The page number to display',
    },
    'a.size': {
      type: 'string',
      description: 'The number of items to display per page',
    },
    'a.sortBy': {
      type: 'enum',
      enumValues: ['metrics.lastActivityDate', 'metrics.visits', 'name'],
      default: 'name',
      description: 'The field to sort by',
    } satisfies RouteSearchParamEnumType<AUDIENCE_MEMBERSHIP_SORT_KEYS>,
    'a.sortOrder': {
      type: 'enum',
      enumValues: ['asc', 'desc'],
      default: 'asc',
      description: 'The sort order',
    },
    'a.search': {
      type: 'string',
      description: 'A string to search for in the audience name',
    },
  },
} satisfies NavigableRouteType<PAGINATION_PARAM_KEYS<'a'>>;

export const audienceDetailEdit = {
  path: `${instance.path}/audiences/:audience/edit`,
  description: 'A form where the user can edit the audience name and description.',
  navigable: true,
  pathParams: {
    audience: {
      type: 'string',
      description: 'The ID of the audience the user wishes to edit',
      required: true,
      requiresLookup: true,
      lookupType: 'audience',
    },
  },
  searchParams: {},
} satisfies NavigableRouteType;

export const audienceById = (audienceId?: string) => `${audiences.path}/${audienceId}`;
export const audienceEditById = (audienceId?: string) => `${audienceById(audienceId)}/edit`;
